import { QueryObserverResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { AttachedFile, ErrorInfo } from '../../../../api/model';
import { useDefaultOptions } from '../../../../hooks/api';
import useContextMenu from '../../../../hooks/useContextMenu';

import { useGetConfirmationRequestDeleteFile } from '../../../../api/confirmation-requests/confirmation-requests';
import { executeDownload } from '../../../../utils/download';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmInputModal from '../../../modules/ConfirmInputModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface AttachedFileDownloaderProps {
  attachedFile: AttachedFile;
  refetch: () => Promise<QueryObserverResult<AxiosResponse<Blob>, AxiosError<Blob>>>;
  error: AxiosError<Blob> | null;
  mode: string;
  refetchDetail: () => void;
}

function AttachedFileDownloader({ attachedFile, refetch, error, mode, refetchDetail }: AttachedFileDownloaderProps) {
  const [displayError, setDisplayError] = useState(false);

  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  // コンテキストメニュー
  const { points, setPoints } = useContextMenu();
  const [contextOpen, setContextOpen] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });
  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };

  useEffect(() => {
    setDisplayError(error != null);
    setDisplaySpinnerModal(false);
  }, [error]);

  // ファイルダウンロード
  const onAttachedFileClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetch();
    setDisplaySpinnerModal(false);
    if (!response.isError && response.data?.data != null) {
      executeDownload(response.data);
    }
  }, [refetch]);

  return (
    <>
      <span
        className="selectfile"
        key={attachedFile.file_id}
        style={{ cursor: 'pointer' }}
        onClick={() => onAttachedFileClicked()}
        onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          event.preventDefault();
          setPoints({ x: event.pageX, y: event.pageY });
          handleContextMenu(event);
        }}
      >
        {['xls', 'xlsx', 'xlsm', 'xltx', 'xltm'].indexOf(String(attachedFile.file_name.split('.').pop())) !== -1 && (
          <i className="fa fa-file-excel" />
        )}
        {['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm'].indexOf(String(attachedFile.file_name.split('.').pop())) !==
          -1 && <i className="fa fa-file-word" />}
        {['ppt', 'pptx', 'pptm', 'ppsx', 'ppsm', 'pps', 'potx', 'potm', 'pot'].indexOf(
          String(attachedFile.file_name.split('.').pop())
        ) !== -1 && <i className="fa fa-file-powerpoint" />}
        {attachedFile.file_name.split('.').pop() === 'csv' && <i className="fa fa-file-csv" />}
        {attachedFile.file_name.split('.').pop() === 'zip' && <i className="fa fa-file-archive" />}
        {attachedFile.file_name.split('.').pop() === 'pdf' && <i className="fa fa-file-pdf" />}
        {attachedFile.file_name.split('.').pop() === 'txt' && <i className="fa fa-file-text" />}
        {['png', 'jpg', 'jpeg', 'eps'].indexOf(String(attachedFile.file_name.split('.').pop())) !== -1 && (
          <i className="fa fa-file-image" />
        )}
        {[
          'xls',
          'xlsx',
          'xlsm',
          'xltx',
          'xltm',
          'doc',
          'docx',
          'docm',
          'dot',
          'dotx',
          'dotm',
          'ppt',
          'pptx',
          'pptm',
          'ppsx',
          'ppsm',
          'pps',
          'potx',
          'potm',
          'pot',
          'csv',
          'zip',
          'pdf',
          'txt',
          'png',
          'jpg',
          'jpeg',
          'eps',
        ].indexOf(String(attachedFile.file_name.split('.').pop())) === -1 && <i className="fa fa-file" />}
        {attachedFile.file_name}
      </span>
      {mode === 'Confirmation' && (
        <ul
          className="pop"
          style={{
            display: contextOpen ? 'block' : '',
            position: 'fixed',
            top: `${points.y}px`,
            left: `${points.x}px`,
          }}
          ref={ref}
        >
          {attachedFile.file_id && <AttachedFileDelete fileId={attachedFile.file_id} refetchDetail={refetchDetail} />}
        </ul>
      )}

      {displaySpinnerModal && <SpinnerModal />}
      {displayError && (
        <BlobErrorModal
          error={error}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayError(false);
          }}
        />
      )}
    </>
  );
}

export default AttachedFileDownloader;

interface AttachedFileDeleteProps {
  fileId: number;
  refetchDetail: () => void;
}

/**
 * 添付ファイル表示，削除コンポーネント
 * @param param0
 * @returns
 */
function AttachedFileDelete({ fileId, refetchDetail }: AttachedFileDeleteProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);

  // 確認モーダル
  const [isConfirm, setIsConfirm] = useState(false);

  // 削除API関連
  const [dispErrorModal, setDispErrorModal] = useState(false);
  const { mutateAsync, error, isError, isLoading } = useGetConfirmationRequestDeleteFile<
    AxiosError<ErrorInfo>,
    unknown
  >(options);

  useEffect(() => setDispErrorModal(isError), [isError]);

  const onClickDelete = async () => {
    await mutateAsync(
      {
        fileId,
      },
      {
        onSuccess: () => {
          refetchDetail(); // 画面の再描画
        },
      }
    );
  };

  return (
    <>
      <li onClick={() => setIsConfirm(true)} style={{ cursor: 'pointer' }}>
        <i className="fa-solid fa-circle-xmark" />
        {t('削除')}
      </li>
      {isConfirm && (
        <ConfirmInputModal
          keyWord="Delete"
          onClickCancel={() => setIsConfirm(false)}
          onClickOk={() => {
            setIsConfirm(false);

            // 削除処理
            void onClickDelete();
          }}
        >
          {t('添付ファイルを削除します。宜しければ”Delete”と入力してください。')}
        </ConfirmInputModal>
      )}
      {isLoading && <SpinnerModal />}
      {dispErrorModal && <ErrorModal error={error} onClickOk={() => setDispErrorModal(false)} />}
    </>
  );
}
